import { TGoogleMapsStatus } from "../../reducers/GoogleMapsStatusReducer";

/**
 * NG-7511: centralized Google Places (Maps JS) availability detection.
 *
 * `react-google-autocomplete` lazily injects the Maps script on first widget mount with no
 * error/availability signal. We instead inject it once at app startup and surface a definitive
 * status (loading | available | unavailable) via the `onStatusChange` callback so address fields
 * can fall back to manual entry. Because we inject first, the autocomplete library reuses the
 * already-present `window.google` rather than injecting a second time.
 *
 * Failure detection covers all three outage classes:
 * - invalid key / billing / quota → Google calls `window.gm_authFailure`, and an eager Places probe
 *   surfaces the same `REQUEST_DENIED` at startup (see below)
 * - network / DNS / blocked (ad-block) → `script.onerror`
 * - silent never-loads → detection timeout
 *
 * Why the eager probe: an invalid / unauthorized / unbilled key still bootstraps
 * `window.google.maps.places`, so the ready callback alone would optimistically report "available".
 * The auth failure only surfaces when a Places request is actually made — i.e. when the user focuses
 * the first address field — flashing Google's broken prediction dropdown before the flag flips. So on
 * ready we fire one `AutocompleteService.getPlacePredictions` probe and report "available" only for a
 * status that proves predictions round-trip (see `probePlacesAvailability`). Outage statuses are
 * unbilled; a healthy key costs one per-request call, and the verdict is then cached module-side so
 * remounts / HMR reuse it instead of billing another probe.
 *
 * Caching trade-off: the verdict is terminal for the page's lifetime. A transient outage detected at
 * startup therefore keeps every address field in manual-entry mode until a reload, even if Google
 * recovers meanwhile. That is the accepted cost of not billing a Places request per remount —
 * manual entry stays fully usable throughout, so the degraded state never blocks the user.
 */
const GMAPS_SCRIPT_ID = "google-maps-places-sdk";
const GMAPS_READY_CALLBACK = "__1hOnGoogleMapsReady";
const DETECTION_TIMEOUT_MS = 10000;

let hasLoaded = false;
/** Terminal verdict from a completed detection, reused by later calls (see `loadGoogleMaps`). */
let resolvedStatus: TGoogleMapsStatus | null = null;
/** True while a probe is outstanding, so concurrent callers don't each fire a billed request. */
let probeInFlight = false;
/**
 * Every consumer that has asked for a status: the detection initiator, anyone who called while it
 * ran, and anyone who arrived after it resolved. Dropping the mid-flight arrivals would leave them
 * stuck on "loading"; dropping the post-resolution ones would hide a late `gm_authFailure`, which
 * stays authoritative after a verdict is reported. A Set keeps repeat callers from doubling up.
 */
let listeners = new Set<(status: TGoogleMapsStatus) => void>();

const notifyAll = (status: TGoogleMapsStatus): void => {
    listeners.forEach((listener) => listener(status));
};

const isPlacesReady = (): boolean => Boolean((window as any).google?.maps?.places);

/**
 * Fire one eager Places request so a broken key *or a broken prediction transport* is caught at
 * startup instead of on the user's first field focus.
 *
 * NG-7511: the probe allow-lists success rather than deny-listing `REQUEST_DENIED`. The Maps JS
 * bundle and the per-request prediction endpoint are separately reachable: the bundle can parse and
 * bootstrap `window.google.maps.places` while `AutocompletionService.GetPredictions` is blocked or
 * unreachable (ad-block, proxy, partial outage). That returns `UNKNOWN_ERROR`, which a deny-list
 * read as "available" — leaving the user with the exact dead text box this ticket exists to fix.
 * Only `OK` / `ZERO_RESULTS` prove predictions actually round-trip; every other status, a throw, or
 * a missing API surface fails closed to manual entry.
 */
const probePlacesAvailability = (onResult: (status: TGoogleMapsStatus) => void): void => {
    try {
        const placesNs = (window as any).google.maps.places;
        const service = new placesNs.AutocompleteService();
        service.getPlacePredictions({ input: "1" }, (_predictions: unknown, status: string) => {
            const healthy = status === placesNs.PlacesServiceStatus.OK || status === placesNs.PlacesServiceStatus.ZERO_RESULTS;
            onResult(healthy ? "available" : "unavailable");
        });
    } catch {
        onResult("unavailable");
    }
};

/**
 * Wire the guards every detection attempt needs and return its `settle` function:
 * - a detection timeout, so a probe or script that never calls back still resolves
 * - `window.gm_authFailure`, which Google invokes on auth/billing/quota failures regardless of who
 *   loaded the script. It is authoritative and can fire AFTER a probe already reported "available"
 *   (an invalid key still bootstraps `window.google.maps.places`), so it overrides rather than being
 *   swallowed by the settled guard.
 *
 * NG-7511: shared by both entry paths. The already-injected path previously registered neither, so an
 * auth failure arriving after its probe was silently ignored.
 *
 * Only one detection is ever in flight (guarded by `probeInFlight`) and none starts once
 * `resolvedStatus` is set, so overwriting `window.gm_authFailure` here cannot orphan a live handler.
 * That invariant is what makes the unconditional assignment safe — preserve it if the cache changes.
 */
const createDetectionGuards = (): ((status: TGoogleMapsStatus) => void) => {
    let settled = false;
    let authFailed = false;
    let timeoutId = 0;

    const report = (status: TGoogleMapsStatus): void => {
        window.clearTimeout(timeoutId);
        resolvedStatus = status;
        probeInFlight = false;
        notifyAll(status);
    };

    const settle = (status: TGoogleMapsStatus): void => {
        if (settled || authFailed) {
            return;
        }
        settled = true;
        report(status);
    };

    (window as any).gm_authFailure = () => {
        if (authFailed) {
            return;
        }
        authFailed = true;
        report("unavailable");
    };

    timeoutId = window.setTimeout(() => settle("unavailable"), DETECTION_TIMEOUT_MS);

    return settle;
};

/**
 * Status contract: a caller that starts or joins a live detection sees `loading` then a terminal
 * status; a caller arriving after resolution sees the terminal status only.
 */
export const loadGoogleMaps = (apiKey: string, onStatusChange: (status: TGoogleMapsStatus) => void): void => {
    // A previous call already reached a terminal verdict — reuse it rather than firing another billed
    // probe on every remount / HMR cycle.
    if (resolvedStatus) {
        // Still registered: a `gm_authFailure` can arrive after the verdict and must reach this caller.
        listeners.add(onStatusChange);
        onStatusChange(resolvedStatus);
        return;
    }
    // Detection already running: join it. The verdict fans out to every queued listener — returning
    // without queueing would leave this caller waiting on a status that never arrives.
    if (probeInFlight) {
        listeners.add(onStatusChange);
        onStatusChange("loading");
        return;
    }
    // Already injected by a prior call. NG-7511: a present `window.google.maps.places` only proves the
    // bundle parsed — it says nothing about whether predictions still round-trip — so probe instead of
    // assuming "available", under the same guards as the injection path.
    if (isPlacesReady()) {
        onStatusChange("loading");
        probeInFlight = true;
        listeners.add(onStatusChange);
        probePlacesAvailability(createDetectionGuards());
        return;
    }
    if (hasLoaded) {
        return;
    }
    // Deliberately NOT cached as a terminal verdict: the key arrives from tenant config, so an empty
    // one is a not-ready-yet condition. Caching it would pin the app to manual entry even after a
    // later call supplies a valid key.
    if (!apiKey) {
        onStatusChange("unavailable");
        return;
    }
    hasLoaded = true;

    onStatusChange("loading");
    probeInFlight = true;
    listeners.add(onStatusChange);

    const settle = createDetectionGuards();

    (window as any)[GMAPS_READY_CALLBACK] = () => {
        // Callback fires once the JS is parsed; confirm the places library actually attached...
        if (!isPlacesReady()) {
            settle("unavailable");
            return;
        }
        // ...then verify the key is actually authorized before reporting "available" — the script
        // parses and bootstraps `window.google.maps.places` even for a restricted / unbilled key.
        probePlacesAvailability(settle);
    };

    const script = document.createElement("script");
    script.id = GMAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&language=en&callback=${GMAPS_READY_CALLBACK}`;
    script.onerror = () => settle("unavailable");
    document.head.appendChild(script);
};

/** Test-only: resets the module-level injection guard and cached verdict so each test starts fresh. */
export const resetGoogleMapsLoaderForTests = (): void => {
    hasLoaded = false;
    resolvedStatus = null;
    probeInFlight = false;
    listeners = new Set();
};
